import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { AttachmentPreview } from '@/components/AttachmentPreview';
import { Notice } from '@/components/Modal';
import { RichText } from '@/lib/text/RichText';
import type { NoticeCategory, NoticeImage } from '@/lib/types';
import { noticeCategoryLabel } from '@/lib/types';
import { NoticeImageList, type DisplayImage } from '@/pages/news/NoticeImageList';
import { safeHref } from '@/lib/utils/url';
import { useMenuLabel } from '@/lib/menu/useMenuLabel';

/**
 * 공지 한 건의 **본문** — 분류·대상자·작성자 / 내용 / 사진 / 첨부.
 *
 * 공지사항 화면에서 글을 펼쳤을 때와, 글쓰기 모달의 미리보기가 **이것 하나를 쓴다**.
 * 미리보기가 실제와 다르면 미리보기가 아니다 — 그래서 표시 규칙을 여기 한 곳에만 둔다.
 *
 * 댓글은 담지 않는다. 아직 저장되지 않은 글에는 달릴 댓글이 없고, 댓글은 글 id 를 필요로 한다.
 *
 * ## 사진 자리표 — 「글 → 사진 → 글」 (사용자 지시 2026-08-28)
 * 본문에 `[[사진1]]` 이 있으면 **그 자리에** 1번 사진이 선다. 자리표가 없는 사진은 지금까지처럼
 * 글 아래에 붙는다.
 *
 * 서식 있는 글(마크다운·HTML)로 갈아엎지 않은 이유: 그러면 **지금까지 올라간 공지 전부**의 표시
 * 방식이 함께 바뀐다. 자리표는 옛 글에 없으므로 옛 글은 한 글자도 달라지지 않는다.
 *
 * ## 글자 꾸밈도 같은 방식이다 (사용자 지시 2026-09-16)
 * 색·굵기·밑줄·형광·구분선·소제목도 자리표다(`lib/text/richMarkup.ts`). 본문은 여전히 평문이라
 * DB 도 서버도 그대로이고, 앱·메일처럼 못 그리는 자리는 `stripRichMarkup` 으로 걷어내면 된다.
 */

export type ArticleView = {
  category: NoticeCategory;
  authorName: string | null;
  subjectName?: string | null;
  subjectDeptSite?: string | null;
  content: string;
  images?: NoticeImage[];
  /** 이 글의 첨부. 붙인 순서 그대로 선다. */
  attachments?: AttachmentView[];
  /** 첨부 목록을 모르는 자리(옛 목 자료)를 위한 한 칸. 위 목록이 있으면 쓰이지 않는다. */
  attachmentName?: string | null;
  attachmentUrl?: string | null;
  /** 이 공지를 낳은 사내지침(V429). 있으면 첨부 위에 그 지침으로 가는 링크가 선다. */
  guidelineId?: string | null;
};

/**
 * 화면에 세우는 첨부 한 개. 아직 안 올린 파일은 `url` 이 없다(글쓰기 미리보기).
 *
 * `url` 은 **내려받기** 링크고 `previewUrl` 은 **보는** 링크다. 미리보기는 `previewUrl` 만 쓴다 —
 * 내려받기 링크는 `content-disposition: attachment` 로 오므로 `<iframe>` 에 꽂으면 브라우저가
 * 그리지 않고 **받아 버린다**(공지 팝업이 뜰 때마다 파일이 저장됐다, 2026-10-01).
 */
export type AttachmentView = {
  id?: string | null;
  fileName: string | null;
  url: string | null;
  previewUrl?: string | null;
};

/**
 * 첨부 목록 — 공지사항·자산 공지·팝업·미리보기가 **이것 하나를 쓴다**.
 *
 * ## 한 개일 때만 미리보기를 편다
 * 첨부가 여럿인 글에서 파일마다 미리보기를 펴면 본문이 화면 몇 장 아래로 밀린다. 한 개짜리 글은
 * 지금까지와 똑같이 보이고(대부분이 그렇다), 여럿이면 이름 줄로만 선다 — 무엇이 붙었는지는
 * 이름이 말하고, 내용은 눌러서 본다.
 */
export function NoticeAttachmentList({ attachments }: { attachments: AttachmentView[] }) {
  if (attachments.length === 0) return null;

  return (
    <div className="space-y-3">
      {attachments.map((attachment, index) => {
        // 이름 없이 올라간 첨부도 있다(명명규칙이 이름을 못 붙인 옛 파일). 링크째 잃지 않게
        // 한 마디로 대신한다 — 서버가 내려받기 이름을 정할 때와 같은 규칙이다.
        const name = attachment.fileName ?? '첨부';
        const key = attachment.id ?? `${name}-${index}`;

        if (!attachment.url) {
          return (
            <Notice key={key} tone="warning">
              첨부 <strong>{name}</strong> — 파일이 올라가 있지 않습니다.
              「수정」에서 파일을 다시 붙이면 여기서 열립니다.
            </Notice>
          );
        }

        return (
          <div key={key} className="space-y-2">
            {attachments.length === 1 && attachment.previewUrl && (
              <AttachmentPreview url={attachment.previewUrl} name={name} />
            )}
            <a
              href={safeHref(attachment.url)}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: '13px', color: 'var(--accent-light)', fontWeight: 600 }}
            >
              첨부파일 보기 — {name}
            </a>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 글에 세울 첨부 — 목록이 있으면 그대로, 없으면 **한 칸짜리 첨부**를 한 줄로 세운다.
 *
 * 목 자료로 물러선 화면과 이미 배포된 앱 번들이 아직 한 칸을 쓴다. 그 칸에 이름이 남아 있는데
 * 화면에 첨부가 없으면 올린 사람은 자기 파일이 어디로 갔는지 알 길이 없다(서버도 읽는 자리에서
 * 같은 판단을 한다 — `NoticeService#attachmentsOf`).
 */
function attachmentsOf(article: ArticleView): AttachmentView[] {
  if (article.attachments && article.attachments.length > 0) return article.attachments;
  if (!article.attachmentName) return [];
  // 한 칸짜리 첨부는 목 자료에서만 온다 — 그 주소는 외부 링크라 보는 데 그대로 쓴다.
  return [{ fileName: article.attachmentName, url: article.attachmentUrl ?? null, previewUrl: article.attachmentUrl ?? null }];
}

/** 본문에 박히는 사진 자리표. 번호는 앨범에 선 순서(썸네일에 적힌 숫자)다. */
const PHOTO_TOKEN = /\[\[사진(\d+)\]\]/g;

/** `[[사진3]]` 처럼 본문에 넣을 자리표를 만든다. 글쓰기 화면과 규칙을 나눠 갖는다. */
export function photoToken(order: number): string {
  return `[[사진${order}]]`;
}

/**
 * 글 + 사진 — <b>자리표가 있으면 그 자리에</b>, 없는 사진은 글 아래에.
 *
 * <p>공지 본문에서 떼어 냈다(2026-09-03). 게시판 「감사합니다」 도 같은 규칙으로 그리는데,
 * 저쪽에는 분류·대상자·작성자가 <b>없다</b>(익명 게시판이다). 규칙을 베껴 가면 자리표를 고칠 때
 * 한쪽만 고쳐지므로, 두 화면이 이 컴포넌트 하나를 나눠 쓴다.
 */
export function ArticleContent({
  content,
  images = [],
  linkImages = true,
}: {
  content: string;
  images?: DisplayImage[];
  /** 미리보기에서는 끈다 — 아직 저장되지 않은 사진이라 열어 볼 원본이 없다. */
  linkImages?: boolean;
}) {
  const { blocks, usedOrders } = splitByPhotoTokens(content, images.length);

  // 본문에 자리표로 이미 선 사진은 아래에 또 세우지 않는다. 두 번 나오면 그건 배치가 아니다.
  const trailing = images.filter((_, index) => !usedOrders.has(index + 1));

  return (
    <>
      <div className="space-y-3">
        {blocks.map((block, index): ReactNode => {
          if (block.kind === 'text') {
            // 글자 꾸밈(색·굵기·구분선)은 `RichText` 가 푼다 — 자리표가 없는 옛 글은 예전과
            // 똑같이 한 덩이 `pre-wrap` 으로 선다.
            return <RichText key={`text-${index}`} text={block.text} />;
          }
          const image = images[block.order - 1];
          return image ? (
            <NoticeImageList key={`photo-${block.order}-${index}`} images={[image]} linkToOriginal={linkImages} />
          ) : null;
        })}
      </div>

      {/*
        자리표를 안 쓴 사진 — 지금까지처럼 글 아래에 순서대로 선다. 첨부와 자리가 다르다:
        저쪽은 「내려받을 문서」 이고 이쪽은 「보이는 사진」 이다.
      */}
      <NoticeImageList images={trailing} linkToOriginal={linkImages} />
    </>
  );
}

export function NoticeArticleBody({
  article,
  linkImages = true,
  showCategory = true,
  openLinksInNewTab = false,
}: {
  article: ArticleView;
  /** 팝업은 켠다 — 작은 창 안에서 지침 화면으로 넘어가지 않고 새 탭으로 연다. */
  openLinksInNewTab?: boolean;
  /** 미리보기에서는 끈다 — 아직 저장되지 않은 사진이라 열어 볼 원본이 없다. */
  linkImages?: boolean;
  /**
   * 분류 뱃지(「정보」·「안전」…)를 세울지. <b>팝업에서는 끈다</b>(사용자 지시 2026-09-10).
   *
   * <p>게시판에서 이 뱃지는 <b>목록 안의 한 건을 가르는</b> 표지다 — 여러 글이 나란히 선 자리라
   * 무슨 갈래인지가 필요하다. 팝업에는 글이 <b>한 건뿐</b>이라 가를 것이 없고, 그 자리에서
   * 「정보」 는 아무것도 알려 주지 않으면서 본문 위 한 줄을 차지한다.
   */
  showCategory?: boolean;
}) {
  // 뱃지 줄에 세울 것이 하나도 없으면 줄 자체를 두지 않는다 — 빈 줄이 본문 위 여백만 벌린다.
  const hasHeadline = showCategory || Boolean(article.subjectName) || Boolean(article.authorName);

  return (
    <div className="space-y-3">
      {hasHeadline && (
        <div className="flex items-center gap-2 flex-wrap">
          {showCategory && <span className="badge badge-blue">{noticeCategoryLabel(article.category)}</span>}
          {/* 누구의 소식인가. 안 고른 글에는 자리 자체가 없다. */}
          {article.subjectName && (
            <span className="badge badge-gray">
              {article.subjectDeptSite ? `${article.subjectDeptSite} · ` : ''}
              {article.subjectName} 님
            </span>
          )}
          {article.authorName && (
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>작성자 {article.authorName}</span>
          )}
        </div>
      )}

      <ArticleContent content={article.content} images={article.images} linkImages={linkImages} />

      {article.guidelineId && <GuidelineLink guidelineId={article.guidelineId} newTab={openLinksInNewTab} />}

      <NoticeAttachmentList attachments={attachmentsOf(article)} />
    </div>
  );
}

/** 사내지침&매뉴얼에서 함께 올린 공지 → 그 지침 한 건(`/news/guidelines?post=`). */
function GuidelineLink({ guidelineId, newTab }: { guidelineId: string; newTab: boolean }) {
  const boardLabel = useMenuLabel('news_guidelines', '사내지침&매뉴얼');
  const to = `/news/guidelines?post=${encodeURIComponent(guidelineId)}`;
  const style = { fontSize: '13px', color: 'var(--accent-light)', fontWeight: 600 } as const;
  return newTab ? (
    <a href={to} target="_blank" rel="noreferrer" style={style}>
      {boardLabel}에서 보기
    </a>
  ) : (
    <Link to={to} style={style}>
      {boardLabel}에서 보기
    </Link>
  );
}

type Block = { kind: 'text'; text: string } | { kind: 'photo'; order: number };

/**
 * 본문을 자리표 기준으로 자른다.
 *
 * **없는 번호의 자리표는 지운다.** 사진을 뺐는데 자리표가 남은 경우인데, `[[사진3]]` 이라는 글자가
 * 공지에 그대로 찍히는 것보다는 조용히 빠지는 편이 낫다 — 읽는 사람에게는 그것이 오타로 보인다.
 * 글쓴이는 미리보기에서 사진이 빠진 것을 본다.
 */
function splitByPhotoTokens(content: string, imageCount: number): { blocks: Block[]; usedOrders: Set<number> } {
  const blocks: Block[] = [];
  const usedOrders = new Set<number>();

  let cursor = 0;
  for (const match of content.matchAll(PHOTO_TOKEN)) {
    const at = match.index ?? 0;
    const order = Number(match[1]);
    const known = order >= 1 && order <= imageCount && !usedOrders.has(order);

    const before = content.slice(cursor, at);
    if (before.trim()) blocks.push({ kind: 'text', text: before.trim() });
    cursor = at + match[0].length;

    if (known) {
      usedOrders.add(order);
      blocks.push({ kind: 'photo', order });
    }
  }

  const rest = content.slice(cursor);
  if (rest.trim()) blocks.push({ kind: 'text', text: rest.trim() });

  return { blocks, usedOrders };
}
